import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { cloneFormItem, createBlankFormItem, hasRevealValue } from '../utils/formHelpers';

const RepeatableFormField = ({
                                 field,
                                 data,
                                 change,
                                 keyPrefix,
                                 setErrors,
                                 renderRows,
                                 onSearchExistingComponent,
                                 tripCurrency,
                                 itemDefaults,
                                 resolveLookupOptions,
                                 hasError,
                                 errorMessage,
                             }) => {
    const items = (data[field.key] || []).map(cloneFormItem);
    const showItemLabels = items.length > 0;
    const boundsSeededRef = useRef(false);

    const collectSubFieldKeys = (fieldsMap, into = new Set()) => {
        Object.values(fieldsMap || {}).forEach((fieldDef) => {
            fieldDef.subFields?.forEach((sub) => {
                Object.keys(sub.fields || {}).forEach((subKey) => into.add(subKey));
                collectSubFieldKeys(sub.fields, into);
            });
        });
        return into;
    };

    const findFieldDef = (fieldsMap, key) => {
        if (fieldsMap?.[key]) return fieldsMap[key];
        let found = null;
        Object.values(fieldsMap || {}).some((fieldDef) => {
            if (!fieldDef?.subFields) return false;
            return fieldDef.subFields.some((sub) => {
                found = findFieldDef(sub.fields, key);
                return !!found;
            });
        });
        return found;
    };

    const blankForKey = (current) => {
        if (typeof current === 'boolean') return false;
        if (Array.isArray(current)) return [];
        if (typeof current === 'number') return '';
        return '';
    };

    // Merchant accept/reject fields — never wipe on Trip Logistics / Merchant
    // progressive resets (that was flipping Accepted → Pending on edit).
    const MERCHANT_RESPONSE_KEYS = new Set(['status', 'rejection_reason', 'rejected_on']);

    const selectedDepartureIds = (item) => {
        if (Array.isArray(item.departure_date_ids) && item.departure_date_ids.length) {
            return item.departure_date_ids;
        }
        if (item.departure_date_id != null && item.departure_date_id !== '') {
            return [item.departure_date_id];
        }
        return [];
    };

    const resolveSeasonBounds = (item) => {
        if (typeof resolveLookupOptions !== 'function') return { from: '', to: '' };
        const departures = resolveLookupOptions({ optionsSource: 'departure_dates' }) || [];
        const starts = [];
        const ends = [];
        selectedDepartureIds(item).forEach((id) => {
            const dep = departures.find((d) => String(d.value) === String(id));
            if (!dep) return;
            const start = dep.date ? String(dep.date).slice(0, 10) : null;
            const endRaw = dep.return_date || dep.end_date || dep.date;
            const end = endRaw ? String(endRaw).slice(0, 10) : null;
            if (start) starts.push(start);
            if (end) ends.push(end);
        });
        starts.sort();
        ends.sort();
        if (!starts.length) return { from: '', to: '' };
        // Upper bound uses return dates so a single departure is not forced to one day.
        return { from: starts[0], to: ends.length ? ends[ends.length - 1] : starts[starts.length - 1] };
    };

    const clampDateToBounds = (value, from, to) => {
        if (!value) return value;
        const v = String(value).slice(0, 10);
        if (from && v < from) return from;
        if (to && v > to) return to;
        return v;
    };

    const allocationDedupeKey = (merchantId, componentId, depId) => {
        if (merchantId == null || merchantId === '' || componentId == null || componentId === ''
            || depId == null || depId === '') {
            return null;
        }
        return `${merchantId}|${componentId}|${depId}`;
    };

    const usedAllocationKeysExcluding = (list, excludeIndex) => {
        const used = new Set();
        (list || []).forEach((row, i) => {
            if (i === excludeIndex) return;
            selectedDepartureIds(row).forEach((depId) => {
                const key = allocationDedupeKey(row.merchant_id, row.package_component_id, depId);
                if (key) used.add(key);
            });
        });
        return used;
    };

    const suggestRequestedAllocation = (item) => {
        if (typeof resolveLookupOptions !== 'function') return null;
        const merchants = resolveLookupOptions({ optionsSource: 'merchants' }) || [];
        const departures = resolveLookupOptions({ optionsSource: 'departure_dates' }) || [];
        const merchant = merchants.find((m) => String(m.value) === String(item.merchant_id));
        if (!merchant) return null;

        const depIds = selectedDepartureIds(item);
        // Earliest selected departure by date (not first picked in the multi-select).
        const departure = depIds.length
            ? depIds
                .map((id) => departures.find((d) => String(d.value) === String(id)))
                .filter(Boolean)
                .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')))[0]
            : null;
        const departureDate = departure?.date
            || (departure?.label ? String(departure.label).slice(0, 10) : null);
        const departureAlloc = Number(departure?.total_allocation) || 0;
        const logisticsId = item.package_component_id;

        const inventories = Array.isArray(merchant.inventories) ? merchant.inventories : [];
        const matching = inventories.filter((inv) => {
            if (!inv?.date || !departureDate) return false;
            if (String(inv.date).slice(0, 10) !== String(departureDate).slice(0, 10)) return false;
            if (inv.blackout) return false;
            const status = String(inv.status || '').toLowerCase();
            if (status === 'unavailable' || status === 'sold out') return false;
            if (
                logisticsId != null
                && logisticsId !== ''
                && inv.package_component_id != null
                && inv.package_component_id !== ''
            ) {
                return String(inv.package_component_id) === String(logisticsId);
            }
            return true;
        });

        let capacity = matching.reduce((sum, inv) => {
            const available = Number(inv.available_capacity);
            if (Number.isFinite(available) && available > 0) return sum + available;
            const units = Number(inv.available_units);
            if (Number.isFinite(units) && units > 0) return sum + units;
            return sum;
        }, 0);

        // Fallback: asset capacity when no date-matched inventory rows exist.
        if (capacity <= 0 && merchant.hasInventory) {
            capacity = Number(merchant.totalCapacity) || 0;
        }
        if (capacity <= 0) return null;

        if (departureAlloc > 0) {
            return Math.min(capacity, departureAlloc);
        }
        return capacity;
    };

    const handleItemChange = (index, keyOrUpdates, value) => {
        const rawUpdates = (keyOrUpdates && typeof keyOrUpdates === 'object')
            ? keyOrUpdates
            : { [keyOrUpdates]: value };

        // Nested repeatables pass updater functions — resolve them against the latest item.
        const resolveUpdates = (item) => Object.fromEntries(
            Object.entries(rawUpdates).map(([k, v]) => [k, typeof v === 'function' ? v(item?.[k]) : v])
        );

        const applyItemUpdates = (item) => {
            const updates = resolveUpdates(item);
            const nextItem = { ...cloneFormItem(item), ...updates };

            // Services: changing category clears all conditional keys.
            if (Object.prototype.hasOwnProperty.call(updates, 'category')) {
                collectSubFieldKeys(field.fields).forEach((subKey) => {
                    if (!Object.prototype.hasOwnProperty.call(updates, subKey)) {
                        nextItem[subKey] = blankForKey(nextItem[subKey]);
                    }
                });
            }

            // Allocations progressive UX: clearing or changing a parent lookup
            // resets everything nested under that field (first select keeps blanks/defaults).
            Object.keys(updates).forEach((changedKey) => {
                if (changedKey === 'category') return;
                const fieldDef = findFieldDef(field.fields, changedKey);
                if (!fieldDef?.subFields?.length) return;
                const prevVal = item[changedKey];
                const newVal = updates[changedKey];
                const hadValue = hasRevealValue(prevVal);
                const hasValue = hasRevealValue(newVal);
                const valueChanged = Array.isArray(prevVal) || Array.isArray(newVal)
                    ? JSON.stringify(prevVal || []) !== JSON.stringify(newVal || [])
                    : String(prevVal) !== String(newVal);
                if (!hasValue || (hadValue && valueChanged)) {
                    collectSubFieldKeys({ [changedKey]: fieldDef }).forEach((subKey) => {
                        if (Object.prototype.hasOwnProperty.call(updates, subKey)) return;
                        // Keep Accepted/Rejected (and rejection metadata) when editing
                        // an existing allocation — only merchant change resets them below.
                        if (MERCHANT_RESPONSE_KEYS.has(subKey)) return;
                        nextItem[subKey] = blankForKey(nextItem[subKey]);
                    });
                }
            });

            // Changing the merchant on a saved allocation invalidates prior accept/reject.
            if (
                Object.prototype.hasOwnProperty.call(updates, 'merchant_id')
                && item.id
            ) {
                const prevMerchant = item.merchant_id;
                const newMerchant = updates.merchant_id;
                const merchantChanged =
                    prevMerchant !== '' &&
                    prevMerchant != null &&
                    String(prevMerchant) !== String(newMerchant);
                if (merchantChanged) {
                    nextItem.status = 'Pending';
                    nextItem.rejection_reason = '';
                    nextItem.rejected_on = '';
                }
            }

            return nextItem;
        };

        const finalizeItem = (nextItem, siblingList, updates) => {
            const touchedDepartures =
                Object.prototype.hasOwnProperty.call(updates, 'departure_date_ids')
                || Object.prototype.hasOwnProperty.call(updates, 'departure_date_id');
            const touchedMerchant = Object.prototype.hasOwnProperty.call(updates, 'merchant_id');
            const touchedLogistics = Object.prototype.hasOwnProperty.call(updates, 'package_component_id');

            // Drop departure picks that already have a row for same merchant+component.
            if (touchedDepartures || touchedMerchant || touchedLogistics) {
                const used = usedAllocationKeysExcluding(siblingList, index);
                const filtered = selectedDepartureIds(nextItem).filter((depId) => {
                    const key = allocationDedupeKey(
                        nextItem.merchant_id,
                        nextItem.package_component_id,
                        depId,
                    );
                    return !key || !used.has(key);
                });
                nextItem.departure_date_ids = filtered;
                nextItem.departure_date_id = filtered.length ? filtered[0] : '';
            }

            // Dated pricing: keep season bounds aligned to selected departure dates.
            if (touchedDepartures || Object.prototype.hasOwnProperty.call(updates, 'pricing_model')) {
                const { from, to } = resolveSeasonBounds(nextItem);
                nextItem.season_bound_from = from;
                nextItem.season_bound_to = to;
                if ((nextItem.pricing_model || 'Dated') === 'Dated' && from && to) {
                    if (!nextItem.season_from) nextItem.season_from = from;
                    if (!nextItem.season_to) nextItem.season_to = to;
                    nextItem.season_from = clampDateToBounds(nextItem.season_from, from, to);
                    nextItem.season_to = clampDateToBounds(nextItem.season_to, from, to);
                    if (nextItem.season_from && nextItem.season_to && nextItem.season_from > nextItem.season_to) {
                        nextItem.season_to = nextItem.season_from;
                    }
                }
            }

            // Requested Allocation: auto-fill from merchant asset inventory for the
            // selected departure date (and trip logistics when linked). User can override.
            if (
                (touchedMerchant || touchedDepartures || touchedLogistics)
                && !Object.prototype.hasOwnProperty.call(updates, 'min_number')
            ) {
                const suggested = suggestRequestedAllocation(nextItem);
                if (suggested != null) {
                    nextItem.min_number = suggested;
                }
            }

            // Add-ons: AE/SB auto-set Applies To and clear attach-component / non-AE fields.
            if (Object.prototype.hasOwnProperty.call(updates, 'addon_type')) {
                const t = updates.addon_type;
                if (t === 'arrive_early') {
                    nextItem.application_scope = 'before_trip';
                    nextItem.linked_component_id = '';
                    nextItem.itinerary_day_id = '';
                    if (!Array.isArray(nextItem.addon_itinerary_days)) {
                        nextItem.addon_itinerary_days = [];
                    }
                } else if (t === 'stay_behind') {
                    nextItem.application_scope = 'after_trip';
                    nextItem.linked_component_id = '';
                    nextItem.itinerary_day_id = '';
                    if (!Array.isArray(nextItem.addon_itinerary_days)) {
                        nextItem.addon_itinerary_days = [];
                    }
                } else {
                    // Leaving AE/SB — drop extension day schedule
                    nextItem.addon_itinerary_days = [];
                    nextItem.extension_days_count = '';
                    nextItem.number_of_days = '';
                    nextItem.min_extension_days = '';
                    nextItem.max_extension_days = '';
                }
            }

            return nextItem;
        };

        const changedKeys = Object.keys(rawUpdates);
        const itemPrefix = keyPrefix ? `${keyPrefix}.${field.key}.${index}` : `${field.key}.${index}`;
        setErrors?.((prev) => prev.filter((e) => !changedKeys.some((ck) => e.key === `${itemPrefix}.${ck}`)));

        // Updater form: merge into the latest list, not this render's `items`
        // snapshot — a stale callback must not revert sibling items.
        change(field.key, (prevItems) => {
            const base = Array.isArray(prevItems) ? prevItems : items;
            return base.map((item, i) => {
                if (i !== index) return cloneFormItem(item);
                const updates = resolveUpdates(item);
                return finalizeItem(applyItemUpdates(item), base, updates);
            });
        });
    };

    // Seed season picker bounds from departures once lookups are ready (edit hydrate
    // no longer copies stored season_from/to into bounds).
    useEffect(() => {
        if (field.key !== 'allocations' || typeof resolveLookupOptions !== 'function') return;
        if (boundsSeededRef.current) return;
        const departures = resolveLookupOptions({ optionsSource: 'departure_dates' }) || [];
        if (!departures.length || !items.length) return;

        let changed = false;
        const next = items.map((item) => {
            if ((item.pricing_model || 'Dated') !== 'Dated') return item;
            if (!selectedDepartureIds(item).length) return item;
            const { from, to } = resolveSeasonBounds(item);
            if (!from || !to) return item;
            if (item.season_bound_from === from && item.season_bound_to === to) return item;
            changed = true;
            return { ...cloneFormItem(item), season_bound_from: from, season_bound_to: to };
        });
        if (!changed) {
            boundsSeededRef.current = true;
            return;
        }
        boundsSeededRef.current = true;
        change(field.key, next);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot seed when lookups arrive
    }, [field.key, items, resolveLookupOptions]);

    const handleAddItem = () => {
        const blank = createBlankFormItem(field.fields);
        if (tripCurrency) {
            blank.currency = tripCurrency;
        }
        if (itemDefaults && typeof itemDefaults === 'object') {
            Object.entries(itemDefaults).forEach(([key, value]) => {
                if (blank[key] === '' || blank[key] === undefined || blank[key] === null) {
                    blank[key] = value;
                }
            });
        }
        const parentErrorKey = keyPrefix ? `${keyPrefix}.${field.key}` : field.key;
        setErrors?.((prev) => prev.filter((e) => e.key !== parentErrorKey));
        change(field.key, [
            ...items.map(cloneFormItem),
            blank,
        ]);
    };
    const handleRemoveItem = (index) => change(field.key, items.filter((_, i) => i !== index).map(cloneFormItem));
    const handleDuplicateItem = (index) => {
        const source = items[index] || {};
        const { id: _id, _clientKey: _ck, ...rest } = source;
        const duplicated = {
            ...cloneFormItem(rest),
            _clientKey: `dup-${Date.now()}-${index}`,
        };
        const next = items.map(cloneFormItem);
        next.splice(index + 1, 0, duplicated);
        change(field.key, next);
    };

    const renderItemActions = (index) => (
        <MDBox
            className="item-actions"
            display="flex"
            gap={0.5}
            sx={{
                opacity: { xs: 1, sm: 0 },
                transition: 'opacity 0.15s ease',
                '.repeatable-item:hover &': { opacity: 1 },
            }}
        >
            {field.allowDuplicate && (
                <Tooltip title={`Duplicate this ${field.label.toLowerCase()}`}>
                    <IconButton
                        size="small"
                        onClick={() => handleDuplicateItem(index)}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'info.main', bgcolor: 'rgba(21, 101, 200, 0.08)' },
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title={`Remove this ${field.label.toLowerCase()}`}>
                <IconButton
                    size="small"
                    onClick={() => handleRemoveItem(index)}
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main', bgcolor: 'rgba(186, 26, 26, 0.08)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </MDBox>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {items.map((itemData, index) => {
                const itemPrefix = keyPrefix ? `${keyPrefix}.${field.key}.${index}` : `${field.key}.${index}`;
                const itemKey = itemData._clientKey ?? itemData.id ?? `${field.key}-${index}`;

                return (
                    <Box
                        key={itemKey}
                        className="repeatable-item"
                        sx={{
                            border: '1px solid',
                            borderColor: 'grey.400',
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                                borderColor: 'grey.600',
                                boxShadow: '0 2px 10px rgba(17, 42, 82, 0.06)',
                            },
                        }}
                    >
                        {showItemLabels ? (
                            <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: 'grey.200',
                                    borderBottom: '1px solid',
                                    borderColor: 'grey.400',
                                }}
                            >
                                <MDBox display="flex" alignItems="center" gap={1}>
                                    <MDBox
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#D6E3FF',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="medium"
                                            sx={{ color: '#1565C8', fontSize: '0.7rem' }}
                                        >
                                            {index + 1}
                                        </MDTypography>
                                    </MDBox>
                                    <MDTypography variant="subtitle2" fontWeight="medium">
                                        {field.label}
                                    </MDTypography>
                                </MDBox>
                                {renderItemActions(index)}
                            </MDBox>
                        ) : (
                            <MDBox display="flex" justifyContent="flex-end" sx={{ px: 2, pt: 1.5 }}>
                                {renderItemActions(index)}
                            </MDBox>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                            {renderRows(
                                field.rows,
                                field.fields,
                                itemData,
                                (key, val) => handleItemChange(index, key, val),
                                itemPrefix
                            )}
                        </Box>
                    </Box>
                );
            })}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                    onClick={handleAddItem}
                    sx={{
                        borderRadius: 2,
                        p: 1.25,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.75,
                        cursor: 'pointer',
                        color: hasError ? 'error.main' : '#1565C8',
                        border: '1px dashed',
                        borderColor: hasError ? 'error.main' : 'grey.500',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        '&:hover': {
                            bgcolor: hasError ? 'rgba(186, 26, 26, 0.06)' : 'rgba(21, 101, 200, 0.05)',
                            borderColor: hasError ? 'error.main' : '#1565C8',
                        },
                    }}
                >
                    <AddIcon fontSize="small" />
                    <MDTypography variant="button" fontWeight="medium" sx={{ color: 'inherit' }}>
                        {field.addLabel || 'Add Item'}
                    </MDTypography>
                </Box>

                {field.allowImportFromTrips && typeof onSearchExistingComponent === 'function' && (
                    <Box
                        onClick={() => onSearchExistingComponent()}
                        sx={{
                            borderRadius: 2,
                            p: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            cursor: 'pointer',
                            color: 'text.secondary',
                            '&:hover': {
                                color: '#1565C8',
                                bgcolor: 'rgba(21, 101, 200, 0.05)',
                            },
                            transition: 'color 0.15s ease, background-color 0.15s ease',
                        }}
                    >
                        <DownloadOutlinedIcon fontSize="small" />
                        <MDTypography variant="button" fontWeight="medium" sx={{ color: 'inherit' }}>
                            Import from existing trip
                        </MDTypography>
                    </Box>
                )}
            </Box>

            {hasError && errorMessage ? (
                <MDTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errorMessage}
                </MDTypography>
            ) : null}
        </Box>
    );
};

export default RepeatableFormField;